import { AI_USE_CASES, type AIUseCase } from '@/lib/ai/langchain/types'

/**
 * Conversation Quality's calls: the one spend a tenant sees as its own line on the billing
 * page, and the one a super user may bill to the platform instead.
 */
export const CONVERSATION_QUALITY_USE_CASES: readonly AIUseCase[] = [
  AI_USE_CASES.CONVERSATION_QUALITY_REVIEW,
  AI_USE_CASES.CONVERSATION_QUALITY_SUGGESTION,
  AI_USE_CASES.CONVERSATION_QUALITY_TOPIC_MERGE,
  AI_USE_CASES.CONVERSATION_QUALITY_CHANGE_CHECK,
] as const

export function isConversationQualityUseCase(useCase: string): boolean {
  return CONVERSATION_QUALITY_USE_CASES.some((qualityUseCase) => qualityUseCase === useCase)
}

/**
 * The use cases that drive a LangChain model config — i.e. the exact `useCase`
 * strings passed to `LangChainService`. The config page shows
 * {@link REGISTERED_USE_CASES} (this list + `voice_agent`). This is the single
 * source of truth for:
 *   1. the `ai_use_cases` registry seed (migration), and
 *   2. the drift guard test (registry rows must equal REGISTERED_USE_CASES).
 *
 * Deliberately narrower than {@link AI_USE_CASES}: it excludes `voice_agent`
 * (voice runs on ElevenLabs, not LangChain — never passed as a model-config
 * useCase) and `custom` (never passed at runtime). The tools-only slug
 * `voice_agent_reply` (see lib/voice/tools) is NOT a model-config use case and
 * is intentionally absent.
 *
 * ── How to add a new use case ────────────────────────────────────────────────
 *  1. Add the slug member to `AI_USE_CASES` in lib/ai/langchain/types.ts.
 *  2. If it drives a LangChain model config, add it here AND add a matching
 *     seed row in a new migration for `ai_use_cases` (display_name / sort_order).
 *  3. If it is an agent-reply use case, register it in {@link AGENT_REPLY_USE_CASE}
 *     and pass it via `new LangChainService({ useCase, scope })` in that agent.
 *  4. Wire any tools through `ai_tools.available_for_use_cases`.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const MODEL_CONFIG_USE_CASES: readonly AIUseCase[] = [
  AI_USE_CASES.EMAIL_AUTO_REPLY,
  AI_USE_CASES.MESSAGE_AGENT_REPLY,
  AI_USE_CASES.EMAIL_CLASSIFICATION,
  AI_USE_CASES.AUTO_REPLY_PROMPT_GENERATOR,
  AI_USE_CASES.KB_EMAIL_ANALYSIS,
  AI_USE_CASES.KB_SPREADSHEET_SUMMARY,
  AI_USE_CASES.ROLE_PROMPT_COMPOSITION,
  AI_USE_CASES.ONBOARDING_PROMPT_PERSONALIZATION,
  ...CONVERSATION_QUALITY_USE_CASES,
] as const

/**
 * Everything registered in the `ai_use_cases` table and shown on the config
 * page. Superset of {@link MODEL_CONFIG_USE_CASES}: also contains `voice_agent`,
 * which is registered for role management only (roles-only card — voice runs on
 * ElevenLabs and has no LangChain model config).
 */
export const REGISTERED_USE_CASES: readonly AIUseCase[] = [
  ...MODEL_CONFIG_USE_CASES,
  AI_USE_CASES.VOICE_AGENT,
] as const

export type UseCaseAgentGroupKey = 'email_agent' | 'message_agent' | 'voice_agent' | 'platform'

export interface UseCaseAgentGroup {
  key: UseCaseAgentGroupKey
  /** English section heading on the config page. */
  label: string
  useCases: readonly AIUseCase[]
}

/**
 * Grouping of registered use cases by the agent they belong to, rendered as
 * sections on `?page=ai-settings&tab=config`. Group membership is code-derived
 * from the slug (like the slugs themselves); ordering INSIDE a group stays
 * DB-driven via `ai_use_cases.sort_order`.
 */
export const USE_CASE_AGENT_GROUPS: readonly UseCaseAgentGroup[] = [
  {
    key: 'email_agent',
    label: 'Email Agent',
    useCases: [AI_USE_CASES.EMAIL_AUTO_REPLY, AI_USE_CASES.EMAIL_CLASSIFICATION],
  },
  {
    key: 'message_agent',
    label: 'Messaging Agent',
    useCases: [AI_USE_CASES.MESSAGE_AGENT_REPLY, ...CONVERSATION_QUALITY_USE_CASES],
  },
  {
    key: 'voice_agent',
    label: 'Voice Agent',
    useCases: [AI_USE_CASES.VOICE_AGENT],
  },
  {
    key: 'platform',
    label: 'Platform AI',
    useCases: [
      AI_USE_CASES.AUTO_REPLY_PROMPT_GENERATOR,
      AI_USE_CASES.KB_EMAIL_ANALYSIS,
      AI_USE_CASES.KB_SPREADSHEET_SUMMARY,
      AI_USE_CASES.ROLE_PROMPT_COMPOSITION,
      AI_USE_CASES.ONBOARDING_PROMPT_PERSONALIZATION,
    ],
  },
]

/** Agent scope types that support a per-agent model override (text agents). */
export type AgentOverrideScopeType = 'email_agent' | 'message_agent'

/**
 * Maps a text-agent scope type to the LangChain use case whose model the
 * per-agent override block re-points. Voice is excluded — it does not use a
 * LangChain model config.
 */
export const AGENT_REPLY_USE_CASE: Record<AgentOverrideScopeType, AIUseCase> = {
  email_agent: AI_USE_CASES.EMAIL_AUTO_REPLY,
  message_agent: AI_USE_CASES.MESSAGE_AGENT_REPLY,
}
